<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faTimes, faSave, faPlus, faPencil, faTrash, faTrashCan } from '@fortawesome/pro-light-svg-icons';
  import { selectedStudy } from '$stores/selectionStore';
  import { userRole, isReadOnly } from '$stores/sessionStore';

  const dispatch = createEventDispatcher();

  export let showModal = false;
  export let subjectGUID = null;

  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';

  let proxies = [];
  let userTypeOptions = [];
  let isLoading = false;
  let isSaving = false;
  let errorMessage = '';
  let successMessage = '';

  // Form data for creating/editing
  let editingProxy = null;
  let formData = {
    name: '',
    securityKey: '',
    userTypeGUID: ''
  };

  // Modal states
  let showForm = false;
  let showDeleteConfirmation = false;
  let proxyToDelete = null;

  // Check permissions
  $: canEdit = !isReadOnly();

  onMount(async () => {
    await fetchUserTypeOptions();
  });

  $: if (showModal && subjectGUID) {
    fetchProxies();
  }

  async function fetchUserTypeOptions() {
    try {
      const response = await fetch(`${API_BASE_URL}/lookups/FORMUSERTYPE`);
      if (response.ok) {
        const data = await response.json();
        userTypeOptions = data.data || [];
      }
    } catch (error) {
      console.error('Error fetching user type options:', error);
    }
  }

  async function fetchProxies() {
    if (!subjectGUID) return;

    isLoading = true;
    errorMessage = '';

    try {
      const response = await fetch(`${API_BASE_URL}/subjectFormProxies?subjectGUID=${subjectGUID}&pageSize=100`);
      
      if (response.ok) {
        const data = await response.json();
        proxies = data.data || [];
      } else {
        const errorData = await response.json();
        errorMessage = errorData.message || 'Failed to fetch proxies';
      }
    } catch (error) {
      console.error('Error fetching proxies:', error);
      errorMessage = 'Failed to fetch proxies';
    } finally {
      isLoading = false;
    }
  }

  function handleAddProxy() {
    editingProxy = null;
    resetFormData();
    showForm = true;
  }

  function handleEditProxy(proxy) {
    editingProxy = proxy;
    formData = {
      name: proxy.name || '',
      securityKey: proxy.securityKey || '',
      userTypeGUID: proxy.userTypeGUID || ''
    };
    showForm = true;
  }

  function handleDeleteProxy(proxy) {
    proxyToDelete = proxy;
    showDeleteConfirmation = true;
  }

  function resetFormData() {
    formData = {
      name: '',
      securityKey: '',
      userTypeGUID: ''
    };
  }

  function getUserTypeLabel(userTypeGUID) {
    if (!userTypeGUID) return 'N/A';
    const option = userTypeOptions.find(opt => opt.id === userTypeGUID);
    return option ? option.label : userTypeGUID;
  }

  async function handleFormSubmit() {
    if (!canEdit) return;

    // Client-side validation
    if (!formData.name.trim()) {
      errorMessage = 'Name is required';
      return;
    }

    if (!formData.securityKey.trim()) {
      errorMessage = 'Security key is required';
      return;
    }

    // Validate security key format
    if (!/^[a-zA-Z0-9]{4,10}$/.test(formData.securityKey.trim())) {
      errorMessage = 'Security key must be 4-10 alphanumeric characters';
      return;
    }

    if (!formData.userTypeGUID) {
      errorMessage = 'User type is required';
      return;
    }

    isSaving = true;
    errorMessage = '';
    successMessage = '';

    try {
      const url = editingProxy 
        ? `${API_BASE_URL}/subjectFormProxies/${editingProxy._id}`
        : `${API_BASE_URL}/subjectFormProxies`;
      
      const method = editingProxy ? 'PUT' : 'POST';

      const requestBody = {
        ...formData,
        subjectGUID: subjectGUID
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        successMessage = editingProxy ? 'Proxy updated successfully' : 'Proxy created successfully';
        showForm = false;
        editingProxy = null;
        await fetchProxies();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          successMessage = '';
        }, 3000);
      } else {
        const errorData = await response.json();
        errorMessage = errorData.message || 'Failed to save proxy';
      }
    } catch (error) {
      console.error('Error saving proxy:', error);
      errorMessage = 'Failed to save proxy';
    } finally {
      isSaving = false;
    }
  }

  async function confirmDeleteProxy() {
    if (!proxyToDelete || !canEdit) return;

    isSaving = true;
    errorMessage = '';

    try {
      const response = await fetch(`${API_BASE_URL}/subjectFormProxies/${proxyToDelete._id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        successMessage = 'Proxy deleted successfully';
        showDeleteConfirmation = false;
        proxyToDelete = null;
        await fetchProxies();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          successMessage = '';
        }, 3000);
      } else {
        const errorData = await response.json();
        errorMessage = errorData.message || 'Failed to delete proxy';
      }
    } catch (error) {
      console.error('Error deleting proxy:', error);
      errorMessage = 'Failed to delete proxy';
    } finally {
      isSaving = false;
    }
  }

  function closeModal() {
    showModal = false;
    showForm = false;
    showDeleteConfirmation = false;
    editingProxy = null;
    proxyToDelete = null;
    errorMessage = '';
    successMessage = '';
    dispatch('close');
  }
</script>

{#if showModal}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Subject Form Proxies
        </h2>
        <button
          on:click={closeModal}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <!-- Content -->
      <div class="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
        {#if errorMessage}
          <div class="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p class="text-red-800 dark:text-red-200 text-sm">{errorMessage}</p>
          </div>
        {/if}

        {#if successMessage}
          <div class="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
            <p class="text-green-800 dark:text-green-200 text-sm">{successMessage}</p>
          </div>
        {/if}

        <!-- Add Proxy Button -->
        {#if canEdit}
          <div class="mb-4">
            <button
              on:click={handleAddProxy}
              class="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
              disabled={isLoading}
            >
              <FontAwesomeIcon icon={faPlus} class="mr-2" />
              Add Proxy
            </button>
          </div>
        {/if}

        <!-- Proxies List -->
        {#if isLoading}
          <div class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p class="mt-2 text-gray-600 dark:text-gray-400">Loading proxies...</p>
          </div>
        {:else if proxies.length === 0}
          <div class="text-center py-8">
            <p class="text-gray-500 dark:text-gray-400">No proxies found for this subject.</p>
          </div>
        {:else}
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 tracking-wider">
                    Name
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 tracking-wider">
                    Security Key
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 tracking-wider">
                    User Type
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 tracking-wider">
                    Status
                  </th>
                  {#if canEdit}
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 tracking-wider">
                      Actions
                    </th>
                  {/if}
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {#each proxies as proxy}
                  <tr class="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {proxy?.name || 'N/A'}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 font-mono">
                      {'•'.repeat(proxy?.securityKey?.length || 0)}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {getUserTypeLabel(proxy?.userTypeGUID)}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full {proxy.active ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200'}">
                        {proxy.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {#if canEdit}
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div class="flex space-x-2">
                          <button
                            on:click={() => handleEditProxy(proxy)}
                            class="text-gray-900 dark:text-gray-100"
                            title="Edit proxy"
                          >
                            <FontAwesomeIcon icon={faPencil} />
                          </button>
                          <button
                            on:click={() => handleDeleteProxy(proxy)}
                            class="text-gray-900 dark:text-gray-100"
                            title="Delete proxy"
                          >
                            <FontAwesomeIcon icon={faTrashCan} />
                          </button>
                        </div>
                      </td>
                    {/if}
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-700">
        <button
          on:click={closeModal}
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
        >
          Close
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Form Modal -->
{#if showForm}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
      <!-- Form Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {editingProxy ? 'Edit Proxy' : 'Add Proxy'}
        </h3>
        <button
          on:click={() => showForm = false}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <!-- Form Content -->
      <form on:submit|preventDefault={handleFormSubmit} class="p-6">
        <div class="space-y-4">
          <!-- Name -->
          <div>
            <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Name *
            </label>
            <input
              id="name"
              type="text"
              bind:value={formData.name}
              required
              class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
              placeholder="Enter proxy name"
            />
          </div>

          <!-- Security Key -->
          <div>
            <label for="securityKey" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Security Key (PIN) *
            </label>
            <input
              id="securityKey"
              type="text"
              bind:value={formData.securityKey}
              required
              maxlength="10"
              class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 font-mono"
              placeholder="4-10 alphanumeric characters"
            />
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              4-10 alphanumeric characters
            </p>
          </div>

          <!-- User Type -->
          <div>
            <label for="userType" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              User Type *
            </label>
            <select
              id="userType"
              bind:value={formData.userTypeGUID}
              required
              class="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="">Select user type</option>
              {#each userTypeOptions as option}
                <option value={option.id}>{option.label}</option>
              {/each}
            </select>
          </div>
        </div>

        <!-- Form Footer -->
        <div class="flex items-center justify-end space-x-3 mt-6">
          <button
            type="button"
            on:click={() => showForm = false}
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            class="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-medium rounded-md"
          >
            {#if isSaving}
              <div class="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            {:else}
              <FontAwesomeIcon icon={faSave} class="mr-2" />
            {/if}
            {editingProxy ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- Delete Confirmation Modal -->
{#if showDeleteConfirmation}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
      <!-- Confirmation Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Confirm Delete
        </h3>
        <button
          on:click={() => showDeleteConfirmation = false}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <!-- Confirmation Content -->
      <div class="p-6">
        <p class="text-gray-700 dark:text-gray-300">
          Are you sure you want to delete the proxy "<strong>{proxyToDelete?.name}</strong>"?
        </p>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">
          This action cannot be undone.
        </p>
      </div>

      <!-- Confirmation Footer -->
      <div class="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
        <button
          on:click={() => showDeleteConfirmation = false}
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
        >
          Cancel
        </button>
        <button
          on:click={confirmDeleteProxy}
          disabled={isSaving}
          class="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white text-sm font-medium rounded-md"
        >
          {#if isSaving}
            <div class="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          {:else}
            <FontAwesomeIcon icon={faTrash} class="mr-2" />
          {/if}
          Delete
        </button>
      </div>
    </div>
  </div>
{/if}
